import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "spirograph";

const DEFAULT_BG = "#0a0612";
const DEFAULT_CURVE_A = "#f472b6";
const DEFAULT_CURVE_B = "#22d3ee";
const DEFAULT_CURVE_C = "#fde68a";

const STEPS_PER_FRAME = 18;          // how fast the pen draws
const FADE_RATE = 0.012;             // bg overlay alpha — older trails fade out
const RECYCLE_AFTER_FULL = 1.4;      // restart curve once we've gone past 1.4 full cycles
const CURVE_SLOTS = 3;               // concurrent curves drawn in different colors

// A hypotrochoid traces the path of a point attached to a small inner circle
// (radius r) rolling inside a large outer circle (radius R), at distance d
// from the inner circle's centre. R, r, d together control the shape.
function hypotrochoid(R, r, d, t) {
  const k = R - r;
  const x = k * Math.cos(t) + d * Math.cos((k / r) * t);
  const y = k * Math.sin(t) - d * Math.sin((k / r) * t);
  return { x, y };
}

function rand(min, max) { return min + Math.random() * (max - min); }
function randInt(min, max) { return Math.floor(rand(min, max + 1)); }

export default function Spirograph({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_CURVE_A, DEFAULT_CURVE_B, DEFAULT_CURVE_C]);
  const slotsRef = useRef([]);
  const primedRef = useRef(false);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_CURVE_A),
      pickColor(colors, 2, DEFAULT_CURVE_B),
      pickColor(colors, 3, DEFAULT_CURVE_C),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const newSlot = (slotIdx) => {
      // Pick R / r so they share a sensible ratio — the petal count of a
      // hypotrochoid equals R / gcd(R, r). Picking small coprime-ish ints
      // gives clean rosette shapes; picking weirder ratios gives looser
      // mandala-like ones.
      const R = randInt(7, 11);
      const r = randInt(2, R - 2);
      const d = rand(2.6, 5.0);
      const scale = Math.min(width, height) * 0.018; // scale to canvas size
      const totalCycles = (r / Math.max(1, gcd(R, r))) * 2 * Math.PI;
      return {
        slotIdx,
        R, r, d, scale,
        cx: rand(width * 0.25, width * 0.75),
        cy: rand(height * 0.25, height * 0.75),
        rotation: rand(0, Math.PI * 2),
        t: 0,
        // Higher dt = bigger jumps = smoother but coarser line.
        dt: 0.018,
        cycleEnd: totalCycles,
      };
    };

    const seed = () => {
      slotsRef.current = new Array(CURVE_SLOTS);
      for (let i = 0; i < CURVE_SLOTS; i += 1) {
        slotsRef.current[i] = newSlot(i);
        // Stagger their start so they don't all begin from origin at once.
        slotsRef.current[i].t = rand(0, slotsRef.current[i].cycleEnd * 0.5);
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
      primedRef.current = false;
    };

    const tick = () => {
      const [bg, c1, c2, c3] = colorsRef.current;
      const palette = [c1, c2, c3];

      // Translucent overlay → existing strokes age out gradually so the
      // canvas never becomes a solid mass of overlapping lines.
      if (!primedRef.current) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);
        primedRef.current = true;
      } else {
        ctx.fillStyle = rgba(bg, FADE_RATE);
        ctx.fillRect(0, 0, width, height);
      }

      const slots = slotsRef.current;
      ctx.lineCap = "round";
      ctx.lineWidth = 1.4;

      for (let s = 0; s < slots.length; s += 1) {
        const slot = slots[s];
        const color = palette[slot.slotIdx % palette.length];
        ctx.strokeStyle = rgba(color, 0.85);

        ctx.beginPath();
        let started = false;
        for (let i = 0; i < STEPS_PER_FRAME; i += 1) {
          const p = hypotrochoid(slot.R, slot.r, slot.d, slot.t);
          // Apply rotation + translate to canvas coords.
          const cos = Math.cos(slot.rotation);
          const sin = Math.sin(slot.rotation);
          const x = slot.cx + (p.x * cos - p.y * sin) * slot.scale;
          const y = slot.cy + (p.x * sin + p.y * cos) * slot.scale;
          if (!started) {
            ctx.moveTo(x, y);
            started = true;
          } else {
            ctx.lineTo(x, y);
          }
          slot.t += slot.dt;
        }
        ctx.stroke();

        // Once the curve has traced more than its full cycle (with a bit of
        // overshoot for visual completeness), start a new one in this slot.
        if (slot.t > slot.cycleEnd * RECYCLE_AFTER_FULL) {
          slots[s] = newSlot(slot.slotIdx);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    seed();
    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}
